import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT, useLang } from "@/i18n";
import { SocialLinks } from "./Social";
import { QuoteAttachments } from "./QuoteAttachments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const mapsUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const maps = mapsUrl;
export const ADDRESSES = [
  ["Sede amministrativa", "Via Angelo Masini, 12/14", "40126 Bologna (BO)"],
  ["Sede operativa", "Via Michele Utveggio, 7 – Z.I. San Cataldo Scalo", "93100 Caltanissetta (CL)"],
];
export const PHONES = [
  ["Telefono", "051 0830043", "tel:+390510830043"],
  ["Mobile / WhatsApp", "380 3758249", "https://wa.me/393803758249"],
];
export const VAT = "P.IVA IT03694561204";

export const AddressList = ({ className = "", labelClass = "" }) => (
  <div className={className}>
    {ADDRESSES.map(([k, a, c]) => (
      <a key={k} href={maps(`Emma Industrie ${a} ${c}`)} target="_blank" rel="noreferrer" className="group block" data-testid={`address-${k.split(" ")[0].toLowerCase()}`}>
        <p className={`text-xs uppercase tracking-[0.2em] ${labelClass}`}>{k}</p>
        <p className="mt-1 transition-colors group-hover:text-[#D98A52]">{a}<br />{c}</p>
      </a>
    ))}
    <a href="mailto:info@emmaindustrie.it" className="block transition-colors hover:text-[#D98A52]" data-testid="address-email">info@emmaindustrie.it</a>
    {PHONES.map(([k, v, href]) => (
      <a key={k} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block transition-colors hover:text-[#D98A52]" data-testid={`address-${k.split(" ")[0].toLowerCase()}`}>
        {k}: {v}
      </a>
    ))}
    <p>{VAT}</p>
  </div>
);
const TYPES = ["Legno lamellare BSH", "Pannelli X-LAM", "Lamellare curvo / elementi speciali", "Elementi per solaio", "Pannello legno massiccio", "Perline", "Biomassa / Bricchetti", "Trucioli depolverati", "Carpenteria metallica", "Casa in legno", "Stabilimento balneare", "Altro"];
const EMPTY = { name: "", company: "", email: "", phone: "", project_type: "", surface: "", message: "", source: "" };

export const Field = ({ label, id, ...props }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</span>
    {props.textarea ? (
      <textarea id={id} rows={4} className="field" data-testid={`contact-${id}`} {...props} textarea={undefined} />
    ) : (
      <input id={id} className="field" data-testid={`contact-${id}`} {...props} />
    )}
  </label>
);

export const ContactForm = ({ tone = "dark" }) => {
  const t = useT();
  const { lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const uploading = files.some((f) => !f.id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, source: "", ...(e?.detail ?? {}) }));
    const pending = sessionStorage.getItem("pending-prefill");
    if (pending) {
      sessionStorage.removeItem("pending-prefill");
      try { onPrefill({ detail: JSON.parse(pending) }); } catch { /* ignore malformed */ }
    }
    window.addEventListener("prefill-quote", onPrefill);
    return () => window.removeEventListener("prefill-quote", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_type) return toast.error(t("form.errType", "Seleziona la tipologia di progetto"));
    if (uploading) return toast.error(t("att.wait", "Attendi la fine del caricamento degli allegati"));
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, lang, attachment_ids: files.map((f) => f.id) });
      toast.success(t("form.ok", "Richiesta inviata. Il nostro ufficio tecnico ti risponderà a breve."));
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.status === 429 ? t("form.err429", "Troppe richieste, riprova più tardi.") : t("form.err", "Invio non riuscito. Controlla i campi e riprova."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id="quote-form" onSubmit={submit} className={`grid gap-8 sm:grid-cols-2 ${tone === "light" ? "form-light" : ""}`} data-testid="contact-form">
      <Field label={t("form.name", "Nome e cognome *")} id="name" required minLength={2} value={form.name} onChange={set("name")} />
      <Field label={t("form.company", "Azienda / Studio")} id="company" value={form.company} onChange={set("company")} />
      <Field label="Email *" id="email" type="email" required value={form.email} onChange={set("email")} />
      <Field label={t("form.phone", "Telefono")} id="phone" value={form.phone} onChange={set("phone")} />
      <label className="block">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{t("form.type", "Tipologia *")}</span>
        <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
          <SelectTrigger className="mt-[0.6rem] h-auto w-full rounded-none border-0 border-b border-[#F7F5F0]/20 bg-transparent px-0 py-[0.7rem] text-base text-[#F7F5F0] shadow-none focus:border-[#D98A52] focus:ring-0 focus:ring-offset-0" data-testid="contact-project-type">
            <SelectValue placeholder={t("form.select", "Seleziona")} />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#1B1816] text-[#F7F5F0]">
            {TYPES.map((ty) => <SelectItem key={ty} value={ty} data-testid={`contact-type-${ty.split(" ")[0].toLowerCase()}`}>{t(`form.t.${ty}`, ty)}</SelectItem>)}
          </SelectContent>
        </Select>
      </label>
      <Field label={t("form.surface", "Superficie stimata (m²)")} id="surface" inputMode="numeric" value={form.surface} onChange={set("surface")} />
      <div className="sm:col-span-2">
        <Field label={t("form.message", "Descrivi il progetto *")} id="message" textarea required minLength={5} value={form.message} onChange={set("message")} />
      </div>
      <QuoteAttachments files={files} setFiles={setFiles} />
      <div className="flex flex-wrap items-center justify-between gap-6 sm:col-span-2">
        <p className="max-w-sm text-xs text-[#A39E93]">{t("form.privacy", "Inviando accetti il trattamento dei dati per la gestione della richiesta.")}</p>
        <button type="submit" disabled={loading || uploading} className="btn-amber disabled:opacity-60" data-testid="contact-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t("form.submit", "Invia richiesta")} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
};

export const Contact = () => {
  const t = useT();
  return (
  <section id="contatti" className="grain relative bg-[#121110] px-5 py-28 text-[#F7F5F0] sm:px-8 lg:py-40" data-testid="contact-section">
    <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionLabel index="09" dark>{t("contact.label", "Preventivo")}</SectionLabel>
        <MaskLines
          className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
          lines={[t("contact.h1", "Costruiamo"), <span key="q">{t("contact.h2a", "qualcosa che")} <em className="font-serif font-normal text-[#D98A52]">{t("contact.h2em", "resti.")}</em></span>]}
        />
        <Reveal className="mt-12 space-y-6 font-mono text-sm" delay={0.2}>
          {[...ADDRESSES.map(([k, a, c]) => [k, <a key={k} href={maps(`Emma Industrie ${a} ${c}`)} target="_blank" rel="noreferrer" className="transition-colors hover:text-[#D98A52]">{a}<br />{c}</a>]), ["Telefono", <span key="t" className="block space-y-1">{PHONES.map(([k, v, href]) => <a key={k} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="tap block transition-colors hover:text-[#D98A52]" data-testid={`contact-phone-${k.split(" ")[0].toLowerCase()}`}>{v}{k !== "Telefono" ? " · WhatsApp" : ""}</a>)}</span>], ["Email", <span key="m" className="block space-y-1"><a href="mailto:info@emmaindustrie.it" className="tap block transition-colors hover:text-[#D98A52]">info@emmaindustrie.it</a><a href="https://www.emmaindustrie.it" target="_blank" rel="noreferrer" className="tap block transition-colors hover:text-[#D98A52]">www.emmaindustrie.it</a></span>], ["Dati fiscali", VAT], ["Social", <SocialLinks key="s" className="mt-2" btnClass="border-white/20 hover:!bg-[#D98A52] hover:!text-[#121110]" />]].map(([k, v]) => (
            <div key={k} className="border-t border-white/15 pt-4" data-testid={`contact-info-${k.split(" ")[0].toLowerCase()}`}>
              <p className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">{t(`contact.k.${k}`, k)}</p>
              <div className="mt-1">{v}</div>
            </div>
          ))}
        </Reveal>
      </div>
      <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
        <ContactForm />
      </Reveal>
    </div>
  </section>
  );
};
